// =============================================================================
// HTTP STATUS CODE HOOK
// =============================================================================

import { useEffect } from 'react';

/**
 * Hook to set HTTP status codes for SEO and proper server responses
 * @param statusCode - The HTTP status code to set (e.g., 404, 200)
 * @param pathname - The current pathname to check against
 */
export const useHttpStatus = (statusCode: number, pathname?: string) => {
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    // Set the status code in the document head for server-side rendering
    const setStatusMeta = () => {
      // Remove existing status meta tag
      const existingStatusMeta = document.querySelector('meta[name="http-status"]');
      if (existingStatusMeta) {
        existingStatusMeta.remove();
      }

      // Create new status meta tag
      const statusMeta = document.createElement('meta');
      statusMeta.name = 'http-status';
      statusMeta.content = statusCode.toString();
      document.head.appendChild(statusMeta);

      // Also set it as a data attribute on the root element for server detection
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.setAttribute('data-http-status', statusCode.toString());
      }
    };

    // Set robots meta tag for 404 pages
    const setRobotsMeta = () => {
      if (statusCode === 404) {
        const existingRobotsMeta = document.querySelector('meta[name="robots"]');
        if (existingRobotsMeta) {
          existingRobotsMeta.setAttribute('content', 'noindex,nofollow');
        }
      }
    };

    setStatusMeta();
    setRobotsMeta();

    // Cleanup function
    return () => {
      const statusMeta = document.querySelector('meta[name="http-status"]');
      if (statusMeta) {
        statusMeta.remove();
      }
      
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.removeAttribute('data-http-status');
      }
    };
  }, [statusCode, pathname]);
};

/**
 * Hook specifically for 404 pages
 */
export const useNotFoundStatus = (pathname?: string) => {
  return useHttpStatus(404, pathname);
};
